import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { callFn, uploadFile } from '../../lib/api';
import type { Category, Item, Location, ReservationTag } from '../../lib/types';
import { tagNames } from '../../lib/types';
import { useApp } from '../../app/AppContext';
import { DataTable, type Column } from '../../components/DataTable';
import { MediaPicker } from '../../components/MediaPicker';
import { useLightbox, ZoomThumb } from '../../components/Lightbox';

type Toast = (text: string, kind?: 'ok' | 'err' | 'info') => void;
interface ParsedRow { description: string; category?: string; condition?: string; quantity?: number; estimated_value?: number; }

// ─── Add/Edit modal — TOP-LEVEL so a parent re-render (e.g. a toast) does NOT
//     remount it and wipe local state like the image preview. ────────────────
function ItemEditModal({ item, categories, locations, tags, descriptions, toast, onClose, onDone }: {
  item: Item | null; categories: Category[]; locations: Location[]; tags: ReservationTag[]; descriptions: string[]; toast: Toast; onClose: () => void; onDone: () => void;
}) {
  const activeLocs = locations.filter((l) => l.is_active);
  const [tagList, setTagList] = useState<ReservationTag[]>(tags);
  const [tagIds, setTagIds] = useState<string[]>(item?.tags?.map((t) => t.id) ?? []);
  function toggleTag(id: string) {
    setTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }
  async function quickAddTag() {
    const name = window.prompt('New tag name:')?.trim();
    if (!name) return;
    try {
      const t = await callFn<ReservationTag>('upsert_reservation_tag', { name });
      setTagList((prev) => (prev.some((x) => x.id === t.id) ? prev : [...prev, t]));
      setTagIds((prev) => (prev.includes(t.id) ? prev : [...prev, t.id]));
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not add tag', 'err'); }
  }
  const [description, setDescription] = useState(item?.description || '');
  const [categoryId, setCategoryId] = useState(item?.category_id || '');
  const [locationId, setLocationId] = useState(item?.location_id || '');
  const [condition, setCondition] = useState(item?.condition || 'used');
  const [quantity, setQuantity] = useState(item?.quantity_total ?? 1);
  const [value, setValue] = useState(item?.estimated_value != null ? String(item.estimated_value) : '');
  const [mediaId, setMediaId] = useState(item?.media_id || '');
  const [previewSrc, setPreviewSrc] = useState(item?.photo_ref || '');
  const [notes, setNotes] = useState(item?.notes || '');
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);

  // Permanent delete lives in its own popup so the editor stays short.
  const [showDelete, setShowDelete] = useState(false);

  // Click the 76px preview to check the photo full size without leaving the editor.
  const editorLb = useLightbox();

  // Description autocomplete — suggest prior catalog descriptions once ≥3 chars
  // are typed, so admins don't re-key the same item names.
  const [descOpen, setDescOpen] = useState(false);
  const [descActive, setDescActive] = useState(-1);
  const descQuery = description.trim().toLowerCase();
  const descSuggestions = useMemo(() => {
    if (!descOpen || descQuery.length < 3) return [];
    return descriptions
      .filter((d) => d.toLowerCase().includes(descQuery) && d.toLowerCase() !== descQuery)
      .slice(0, 8);
  }, [descOpen, descQuery, descriptions]);
  function chooseDesc(d: string) { setDescription(d); setDescOpen(false); setDescActive(-1); }
  // Description is a single logical line, but long names must stay fully
  // visible while editing — so it's a textarea that wraps and grows with its
  // content (Enter never inserts a newline; pasted line breaks become spaces).
  const descRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = descRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [description]);
  function onDescKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (descSuggestions.length === 0 || descActive < 0)) { e.preventDefault(); return; }
    if (descSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setDescActive((i) => Math.min(i + 1, descSuggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setDescActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && descActive >= 0) { e.preventDefault(); chooseDesc(descSuggestions[descActive]); }
    else if (e.key === 'Escape') { setDescOpen(false); }
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast('Please choose an image file', 'err'); return; }
    setPreviewSrc(URL.createObjectURL(file)); // instant local preview
    setUploading(true);
    try {
      const res = await uploadFile(file);
      const media = await callFn<{ id: string }>('add_media', { url: res.url, filename: res.filename, content_type: res.contentType, size_bytes: res.sizeBytes });
      setMediaId(media.id);
      toast('Image uploaded', 'ok');
    } catch (err) { setPreviewSrc(item?.photo_ref || ''); toast(err instanceof Error ? err.message : 'Upload failed', 'err'); }
    finally { setUploading(false); }
  }

  async function save() {
    if (!description.trim()) { toast('Description required', 'err'); return; }
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        description: description.trim(), category_id: categoryId || null, condition,
        location_id: locationId || null, media_id: mediaId || null, notes: notes.trim() || null,
        estimated_value: value === '' ? null : Number(value),
      };
      payload.tag_ids = tagIds;
      if (item) { payload.id = item.id; payload.quantity_total = quantity; await callFn('update_item', payload); }
      else { payload.quantity = quantity; await callFn('create_item', payload); }
      toast('Saved', 'ok'); onDone();
    } catch (e) { toast(e instanceof Error ? e.message : 'Save failed', 'err'); setBusy(false); }
  }

  return (
    <>
    {/* Intentionally NOT closable via backdrop click — only Cancel/Save dismiss
        this editor, so an accidental outside tap can't discard in-progress edits. */}
    <div className="modal-bg">
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <h3>{item ? 'Edit item' : 'Add item'}</h3>
        <div className="stack">
          <label className="field">Description
            <div className="autocomplete">
              <textarea
                ref={descRef}
                className="desc-input"
                rows={1}
                value={description}
                onChange={(e) => { setDescription(e.target.value.replace(/[\r\n]+/g, ' ')); setDescOpen(true); setDescActive(-1); }}
                onFocus={() => setDescOpen(true)}
                onBlur={() => setTimeout(() => setDescOpen(false), 120)}
                onKeyDown={onDescKey}
                autoComplete="off"
                role="combobox"
                aria-expanded={descSuggestions.length > 0}
              />
              {descSuggestions.length > 0 && (
                <ul className="autocomplete-list" role="listbox">
                  {descSuggestions.map((d, i) => (
                    <li
                      key={d}
                      role="option"
                      aria-selected={i === descActive}
                      className={`autocomplete-item${i === descActive ? ' active' : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); chooseDesc(d); }}
                      onMouseEnter={() => setDescActive(i)}
                    >
                      {d}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </label>
          <div className="row">
            <label className="field">Category
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">—</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="field">Condition
              <select value={condition} onChange={(e) => setCondition(e.target.value as 'new' | 'used')}>
                <option value="new">New</option><option value="used">Used</option>
              </select>
            </label>
          </div>
          <label className="field">Pickup location
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">— choose where this item is —</option>
              {activeLocs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.short_code ? ` (${l.short_code})` : ''}</option>)}
            </select>
          </label>
          <div className="field">Photo
            <div className="photo-row">
              {previewSrc
                ? <ZoomThumb src={previewSrc} className="photo-thumb" caption={description || 'item'} onOpen={editorLb.open} />
                : <div className="photo-thumb empty">🐾</div>}
              <div className="stack" style={{ flex: 1, gap: 6 }}>
                <input type="file" accept="image/*" onChange={onPickFile} disabled={uploading} />
                {uploading && <span className="page-sub" style={{ margin: 0 }}>Uploading…</span>}
                <div className="row" style={{ gap: 8 }}>
                  <button type="button" className="btn secondary small" onClick={() => setPicking(true)}>Choose from library</button>
                  {(mediaId || previewSrc) && !uploading && <button type="button" className="btn ghost small" onClick={() => { setMediaId(''); setPreviewSrc(''); }}>Remove</button>}
                </div>
              </div>
            </div>
          </div>
          {picking && <MediaPicker onClose={() => setPicking(false)} onSelect={(m) => { setMediaId(m.id); setPreviewSrc(m.url); setPicking(false); }} />}
          <div className="row">
            <label className="field">Quantity
              <input type="number" min={0} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} />
            </label>
            <label className="field">Est. value ($)
              <input type="number" min={0} step="0.01" value={value} onChange={(e) => setValue(e.target.value)} placeholder="optional" />
            </label>
          </div>
          <label className="field">Notes (optional)
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. open bag ~50% left, best-by April 2026, needs cleaning" />
          </label>
          <div className="field">Tags
            <div className="tag-select">
              {tagList.map((t) => {
                const on = tagIds.includes(t.id);
                return (
                  <button type="button" key={t.id} className={`tag-toggle${on ? ' on' : ''}`}
                    style={on ? { background: t.color || '#2e6da4', borderColor: t.color || '#2e6da4', color: '#fff' } : undefined}
                    onClick={() => toggleTag(t.id)}>
                    {t.name}
                  </button>
                );
              })}
              <button type="button" className="tag-toggle add" onClick={quickAddTag}>+ New tag</button>
            </div>
          </div>
          <div className="row">
            <button className="btn secondary" onClick={onClose}>Cancel</button>
            <button className="btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>

          {item && (
            <div className="danger-zone">
              <button type="button" className="btn danger ghost" onClick={() => setShowDelete(true)}>Permanently delete…</button>
            </div>
          )}
        </div>
      </div>
    </div>
    {item && showDelete && (
      <DeleteItemModal item={item} toast={toast} onClose={() => setShowDelete(false)} onDeleted={onDone} />
    )}
    {editorLb.node}
    </>
  );
}

// Permanent-delete confirmation popup — separate modal so the editor stays short.
// Requires typing the item's exact name to enable the delete.
function DeleteItemModal({ item, toast, onClose, onDeleted }: {
  item: Item; toast: Toast; onClose: () => void; onDeleted: () => void;
}) {
  const [text, setText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const ready = text.trim() === (item.description || '').trim();
  async function del() {
    if (!ready) return;
    setDeleting(true);
    try {
      await callFn('delete_item', { id: item.id, confirm_name: text.trim() });
      toast('Item permanently deleted', 'ok');
      onDeleted();
    } catch (e) { toast(e instanceof Error ? e.message : 'Delete failed', 'err'); setDeleting(false); }
  }
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Permanently delete item?</h3>
        <div className="stack">
          <div className="notice err" style={{ margin: 0 }}>
            <b>This permanently deletes the item and all of its history</b> (reservations and activity log) from the database. This cannot be undone.
          </div>
          <label className="field">Type the item name to confirm: <b>{item.description}</b>
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Item name" autoFocus />
          </label>
          <div className="row">
            <button type="button" className="btn secondary" onClick={onClose}>Keep item</button>
            <button type="button" className="btn danger" onClick={del} disabled={!ready || deleting}>
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ locations, toast, onClose, onDone }: {
  locations: Location[]; toast: Toast; onClose: () => void; onDone: () => void;
}) {
  const activeLocs = locations.filter((l) => l.is_active);
  const [text, setText] = useState('');
  const [source, setSource] = useState('csv');
  const [defaultLoc, setDefaultLoc] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<ParsedRow[] | null>(null);

  function parse() {
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) { setPreview([]); return; }
    const first = lines[0].toLowerCase();
    const hasHeader = /descrip|item|categor|qty|quantity|condition|value/.test(first);
    const body = hasHeader ? lines.slice(1) : lines;
    const rows: ParsedRow[] = body.map((line) => {
      const cells = line.split(/\t|,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map((c) => c.replace(/^"|"$/g, '').trim());
      const nums = cells.map((c) => Number(c)).map((n) => (Number.isFinite(n) ? n : null));
      const qtyIdx = nums.findIndex((n) => n != null && Number.isInteger(n));
      return {
        description: cells[0] || '',
        category: cells[1] && Number.isNaN(Number(cells[1])) ? cells[1] : undefined,
        condition: cells.find((c) => /^(new|used)$/i.test(c))?.toLowerCase(),
        quantity: qtyIdx >= 0 ? Number(cells[qtyIdx]) : 1,
      };
    }).filter((r) => r.description);
    setPreview(rows);
  }

  async function doImport() {
    if (!preview || preview.length === 0) { toast('Nothing to import — paste rows and Preview first', 'err'); return; }
    setBusy(true);
    try {
      const res = await callFn<{ inserted: number }>('import_items', { source, rows: preview, default_location_id: defaultLoc || null });
      toast(`Imported ${res.inserted} items`, 'ok'); onDone();
    } catch (e) { toast(e instanceof Error ? e.message : 'Import failed', 'err'); setBusy(false); }
  }

  return (
    // Not closable via backdrop click — only Cancel/Import dismiss it, so an
    // accidental outside tap can't discard a pasted/loaded spreadsheet.
    <div className="modal-bg">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h3>Import items</h3>
        <p className="page-sub" style={{ marginTop: 0 }}>
          Paste rows from CSV, Excel, or a Google Sheet (tab- or comma-separated).
          Columns: <b>description, category, condition, quantity</b>. A header row is detected automatically.
        </p>
        <div className="stack">
          <div className="row">
            <label className="field">Source
              <select value={source} onChange={(e) => setSource(e.target.value)}>
                <option value="csv">CSV</option><option value="xlsx">Excel</option><option value="google_sheets">Google Sheets</option>
              </select>
            </label>
            <label className="field">Pickup location (applied to all rows)
              <select value={defaultLoc} onChange={(e) => setDefaultLoc(e.target.value)}>
                <option value="">— set later —</option>
                {activeLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
          </div>
          <textarea rows={7} placeholder={'Fancy Feast Grilled Chicken, Wet Food - Adult, new, 24\nGray tunnel, Toys, used, 1'}
            value={text} onChange={(e) => { setText(e.target.value); setPreview(null); }} />
          {preview && <div className="notice info">{preview.length} rows ready to import.</div>}
          <div className="row">
            <button className="btn secondary" onClick={onClose}>Cancel</button>
            {!preview ? <button className="btn" onClick={parse}>Preview</button>
              : <button className="btn" onClick={doImport} disabled={busy}>{busy ? 'Importing…' : `Import ${preview.length}`}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Catalog() {
  const { categories, locations, reservationTags, toast } = useApp();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Item | 'new' | null>(null);
  const [importing, setImporting] = useState(false);

  async function load() {
    setLoading(true);
    try { setItems(await callFn<Item[]>('list_items', { all: true })); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed to load', 'err'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  async function closeItem(id: string) {
    if (!confirm('Mark this item as no longer available?')) return;
    try { await callFn('close_item', { id }); toast('Item closed', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  async function restoreItem(id: string) {
    try { await callFn('restore_item', { id }); toast('Item restored', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  // Unique prior descriptions for the item-editor typeahead.
  const descriptions = useMemo(
    () => Array.from(new Set(items.map((i) => i.description).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [items]);

  const lb = useLightbox();

  const catalogCols: Column<Item>[] = [
    {
      key: 'description', label: 'Description', get: (it) => it.description,
      render: (it) => (
        <span className="cell-with-thumb">
          {it.photo_ref
            ? <ZoomThumb src={it.photo_ref} className="row-thumb" caption={it.description} onOpen={lb.open} />
            : <span className="row-thumb empty">🐾</span>}
          {it.description}
        </span>
      ),
    },
    { key: 'category', label: 'Category', get: (it) => it.category_name || '—' },
    { key: 'location', label: 'Location', get: (it) => it.location_name || 'unset', render: (it) => it.location_name || <span className="badge gray">unset</span> },
    { key: 'condition', label: 'Cond.', get: (it) => it.condition, render: (it) => <span className={`badge ${it.condition}`}>{it.condition}</span> },
    { key: 'avail', label: 'Avail.', get: (it) => it.quantity_available, numeric: true, align: 'right', render: (it) => `${it.quantity_available}/${it.quantity_total}` },
    { key: 'value', label: 'Value', get: (it) => (it.estimated_value != null ? Number(it.estimated_value) : -1), numeric: true, align: 'right', render: (it) => (it.estimated_value != null ? `$${Number(it.estimated_value).toFixed(2)}` : '—') },
    { key: 'status', label: 'Status', get: (it) => it.status, render: (it) => <span className={`badge ${it.status === 'available' ? 'new' : 'gray'}`}>{it.status}</span> },
    {
      key: 'tags', label: 'Tags', get: (it) => tagNames(it.tags),
      render: (it) => (it.tags && it.tags.length > 0
        ? <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {it.tags.map((t) => <span key={t.id} className="tag-chip" style={{ background: t.color || '#dce6f1' }}>{t.name}</span>)}
          </div>
        : <span className="muted">—</span>),
    },
  ];

  return (
    <>
      <h1 className="page-title">Catalog</h1>
      <p className="page-sub">Add donated items, edit, and bulk-import the donation spreadsheet.</p>
      <div className="toolbar">
        <button className="btn" onClick={() => setEditing('new')}>+ Add item</button>
        <button className="btn secondary" onClick={() => setImporting(true)}>Import spreadsheet</button>
        <div className="spacer" />
        <span className="page-sub" style={{ margin: 0 }}>{items.length} listings</span>
      </div>

      {loading ? <div className="loading">Loading…</div> : (
        <DataTable
          columns={catalogCols}
          rows={items}
          rowKey={(it) => it.id}
          exportName="catalog"
          empty="No items yet — add one or import the spreadsheet."
          actions={(it) => (
            <>
              <button className="btn ghost small" onClick={() => setEditing(it)}>Edit</button>
              {it.status === 'available'
                ? <button className="btn ghost small" onClick={() => closeItem(it.id)}>Close</button>
                : <button className="btn ghost small" onClick={() => restoreItem(it.id)}>Restore</button>}
            </>
          )}
        />
      )}

      {editing && <ItemEditModal item={editing === 'new' ? null : editing} categories={categories} locations={locations} tags={reservationTags} descriptions={descriptions} toast={toast} onClose={() => setEditing(null)} onDone={() => { setEditing(null); void load(); }} />}
      {importing && <ImportModal locations={locations} toast={toast} onClose={() => setImporting(false)} onDone={() => { setImporting(false); void load(); }} />}
      {lb.node}
    </>
  );
}
